<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .modal-content {
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(16px);
    padding: utils.in_rem(16px);

    .empty-state {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: utils.in_rem(32px) 0;
      text-align: center;
    }
  }

  .footer {
    display: flex;
    gap: utils.in_rem(8px);
    justify-content: flex-end;
    margin-top: utils.in_rem(16px);
  }
</style>

<script>
  import { onMount } from "svelte";
  // Stores
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import {
    activePopUpModalStore,
    candidateProjectsStore,
    candidateStore,
  } from "../../stores";
  // Utils
  import { showToast, ToastError } from "@sourcewhale/core";
  import {
    associateToProject,
    getAllProjects,
    modifyProject,
  } from "../../../../../api/projects";
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Modal from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/Modal.svelte";
  import SingleSelectDropdown from "../../../../components/deprecated/SingleSelectDropdown.svelte";

  let selectedProjectId = $state(null);
  let addToProjectAs = $state("candidate"); // Default to adding as candidate

  let submitting = $state(false);
  let loading = $state(true);
  let projects = $state([]);

  // Format project items for the dropdown
  let projectItems = $derived(
    projects.map((project) => ({
      id: project.projectId,
      label: project.projectName,
      icon: "fa-regular fa-folder",
    }))
  );

  // Fetch projects on mount
  onMount(async () => {
    try {
      loading = true;
      const result = await getAllProjects();
      projects = result || [];
    } catch (err) {
      if (err instanceof ToastError) {
        err.title = "Failed to load projects";
        showToast(err);
        return;
      }
      throw err;
    } finally {
      loading = false;
    }
  });

  const confirm = () => {
    activePopUpModalStore.set(null);
  };
  const cancel = () => {
    activePopUpModalStore.set(null);
  };

  const wrappedConfirm = async () => {
    if (!selectedProjectId) return;

    const candidateId = $candidateStore.candidateId;
    if (!candidateId) return;

    submitting = true;

    // Non CRM users can only add as candidate
    if (!$userStore.hasCrm) addToProjectAs = "candidate";

    try {
      if (addToProjectAs === "clientContact") {
        await modifyProject({
          projectId: selectedProjectId,
          kind: "create-client-contact",
          projectClientContactId: candidateId,
        });
      } else if (addToProjectAs === "candidate") {
        await associateToProject({
          add: { [selectedProjectId]: [candidateId] },
        });
      }

      // Refresh the projects table to show the new association
      candidateProjectsStore.get();

      showToast({
        title: `Contact added to project successfully`,
        type: "success",
      });
    } catch (err) {
      showToast({
        type: "danger",
        title: "Failed to add contact to project",
        subtitle: err.message,
      });
    } finally {
      submitting = false;
      confirm();
    }
  };
</script>

<Modal size="small" onclose={cancel} blur={true} closeButton={false}>
  <div slot="header"><h2>Add Contact To Project</h2></div>
  <div class="modal-content">
    {#if loading}
      <span>Loading...</span>
    {:else if projects.length === 0}
      <div class="empty-state">
        <p>No projects available. Please create a project first.</p>
      </div>
    {:else}
      <SingleSelectDropdown
        placeholder="Select a project"
        items={projectItems}
        bind:selectedItem={selectedProjectId}
        showFilter={true}
      />
      {#if $userStore.hasCrm}
        <SingleSelectDropdown
          items={[
            { id: "candidate", label: "Add as Candidate" },
            { id: "clientContact", label: "Add as Client Contact" },
          ]}
          bind:selectedItem={addToProjectAs}
          showFilter={false}
        />
      {/if}
    {/if}
    <div class="footer">
      <Button
        kind="ghost-blue"
        onclick={cancel}
        disabled={submitting}
        class={submitting ? "disabled" : ""}
      >
        Cancel
      </Button>
      <Button
        kind="primary"
        onclick={wrappedConfirm}
        disabled={!selectedProjectId || submitting}
        loading={submitting}
        class={!selectedProjectId || submitting ? "disabled" : ""}
      >
        Add to Project
      </Button>
    </div>
  </div>
</Modal>
